import { type CurrencyOption } from '@shared/config';

import { type InvestStatus } from './cross-types';

/** 회사 상태 (코드: PortfolioStatusOption). `@x/investment` 로 StockCard 가 쓴다 */
export type PortfolioStatus =
  | 'None'
  | 'PartialExit'
  | 'CompleteExit'
  | 'Shutdown'
  | 'PreShutdown'
  | 'CompleteImpairment'
  | 'PartialImpairment';

/** 섹터 */
export const SECTOR_OPTIONS = [
  'AbleTech',
  'BioHealthcare',
  'WaterSmartcity',
  'ClimateTech',
  'EnergyCleanTech',
  'EdTech',
  'FoodAgriTech',
  'FinTech',
  'CircularEco',
  'SocialInfra',
] as const;
export type SectorOption = (typeof SECTOR_OPTIONS)[number];

export const SECTOR_LABEL: Record<SectorOption, string> = {
  AbleTech: 'Able Tech',
  BioHealthcare: 'Bio/Healthcare',
  WaterSmartcity: 'Water/Smartcity',
  ClimateTech: 'Climate Tech',
  EnergyCleanTech: 'Energy/CleanTech',
  EdTech: 'EdTech',
  FoodAgriTech: 'Food/AgriTech',
  FinTech: 'FinTech',
  CircularEco: 'Circular/Eco',
  SocialInfra: 'Social Infra',
};

/** 회사 담당자 */
export interface PortfolioMember {
  idx: number;
  name: string;
  email: string;
  mobile: string;
}

/** 담당 심사역(표시용) */
export interface PortfolioManagerContact {
  memberIdx: number;
  /** ManageInternal = 사후관리(내부), Internal = 내부관리(DD-74) — 둘은 D3 만 본다 */
  type: 'Dealsourcing' | 'Diligence' | 'ManageVics' | 'ManageInternal' | 'Internal';
  name: string;
  mobile: string | null;
  email: string;
}

/** KPI 항목. isMajor: true = 비즈니스 KPI, false = 임팩트 KPI */
export interface PortfolioKpi {
  idx: number;
  isMajor: boolean;
  title: string;
  /** 없으면 null 그대로 — 정보 수정이 손대지 않은 KPI 의 NULL 을 '' 로 바꾸지 않게(구·신 비교 CHK-S10-19) */
  comment: string | null;
}

/** 포트폴리오(회사) 상세 */
/** 타회사 담당 심사역 한 줄(계약 ExternalManager) */
export interface PortfolioExternalManager {
  company: string | null;
  name: string | null;
  mobile: string | null;
  email: string | null;
}

export interface PortfolioDetail {
  idx: number;
  name: string;
  username: string;
  address: string;
  businessNumber: string;
  corporationNumber: string;
  companySector: SectorOption | null;
  /** 이미지 URL */
  companyLogo: string | null;
  homepageUrl: string | null;
  /** YYYY-MM-DD */
  establishedAt: string | null;
  ceoName: string | null;
  ceoMobile: string | null;
  ceoEmail: string | null;
  member: PortfolioMember[];
  manager: PortfolioManagerContact[];
  /** 타회사 담당 심사역(info_portfolio.3rd_member_data). 정보 수정 폼이 이 값으로 시작한다 */
  externalManagers: PortfolioExternalManager[];
  commentInvestmentHighlight: string | null;
  commentInvestmentBusiness: string | null;
  commentImpactUNDGs: string | null;
  commentImpactProblem: string | null;
  commentImpactSolution: string | null;
  commentGraphRevenue: string | null;
  commentGraphEquivalent: string | null;
  commentGraphEmployee: string | null;
  commentGraphMajorKPI: string | null;
  commentGraphMinorKPI: string | null;
  kpi: PortfolioKpi[];
  isUsingConnect: boolean;
  status: InvestStatus;
  defaultCurrency: CurrencyOption;
  totalValuation: number;
  totalValuationExchangeRate: number;
  remainValuation: number;
  remainValuationExchangeRate: number;
  valuatedAt: string | null;
  irr: number | null;
  multiple: number | null;
}

/** 목록 안의 회사 하나(이전 · 다음 이동) */
export interface PortfolioRef {
  idx: number;
  name: string;
}
